import { type ReactNode } from "react";
import { useLiveStream } from "../state/live-stream.tsx";
import { type ConnectionState } from "../state/live-stream";

/**
 * The connection indicator (design 12.2; FR-13 AC3: display the live connection state).
 *
 * It reads the live-stream store's snapshot and renders a SINGLE status dot — no "Live" word and no
 * second dot. The dot's colour (and, for assistive tech, its text) is the whole signal:
 *   - connected                → a glowing, slowly-blinking GREEN dot (the control plane is live).
 *   - connecting               → a steady GREY dot (an issue: the stream is not fully up yet).
 *   - disconnected / error     → a steady RED dot (a severe issue: the stream is down).
 *
 * Status is still conveyed by MORE than colour: a visually-hidden `role="status"` carries the full
 * state in words (design 12.4, NFR-7 AC3), and the `data-severity` attribute (not the colour) is
 * what the CSS keys off, so the three severities stay distinct in both themes. "Unverifiable is
 * never green": only an actually-connected stream shows the green dot.
 */

/** The three severities the single dot can show, mapped from the raw connection state. */
type DotSeverity = "ok" | "warn" | "severe";

const SEVERITY: Readonly<Record<ConnectionState, DotSeverity>> = {
  connected: "ok",
  connecting: "warn",
  disconnected: "severe",
  error: "severe",
};

const LABEL: Readonly<Record<ConnectionState, string>> = {
  connected: "Live",
  connecting: "Connecting",
  disconnected: "Offline",
  error: "Disconnected",
};

export function ConnectionIndicator(): ReactNode {
  const { connection, transport, detail } = useLiveStream();
  const label = LABEL[connection];
  const severity = SEVERITY[connection];
  const transportSuffix = transport === "sse" ? " (SSE)" : "";
  const title = detail ?? `Live stream: ${label}${transportSuffix}`;

  return (
    <span className="conn" data-severity={severity} title={title}>
      <span className="conn__dot" aria-hidden="true" />
      {/* The full state as text for assistive technology (status by more than colour). */}
      <span className="sr-only" role="status">
        {title}
      </span>
    </span>
  );
}
